import React, { useState } from 'react';
import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { renderToString } from 'react-dom/server';
import { titleize } from 'inflection';
import Chart from 'react-apexcharts';
import moment from 'moment';

import Button, { BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import ChartTooltipIdea from '@buyside/components/presentation/Charts/ChartTooltip/ChartTooltipIdea/chartTooltipIdea';
import ChartTooltipIdeaUpdate from '@buyside/components/presentation/Charts/ChartTooltip/ChartTooltipIdeaUpdate/chartTooltipIdeaUpdate';
import ChartTooltipPrice from '@buyside/components/presentation/Charts/ChartTooltip/ChartTooltipPrice/chartTooltipPrice';
import DelayedAccessAlert, {
  Location
} from '@buyside/components/presentation/AccessControl/delayedAccessAlert';
import TabbedNav from '@buyside/components/presentation/TabbedNav/tabbedNav';

import {
  graphqlTimeframe,
  readableTimeframe,
  timeframes
} from '@buyside/components/presentation/CompanyShowPage/CompanyBuysideConsensus/companyChartTimeframeHelper';
import { getTooltipData } from '@buyside/util/assetDataHelper';

import { Maybe, useAssetQuoteQuery } from '@interfaces/graphql-typings';

import styles from './priceChartStyles.scss';

const PriceChart: React.FC<{
  assetId: string;
  currencyCode: Maybe<string>;
  primarySymbol: Maybe<string>;
  entityName: Maybe<string>;
  showUserDetails: boolean;
  toggleMainArea?: (boolean: boolean) => void;
}> = ({ assetId, currencyCode, primarySymbol, entityName, toggleMainArea, showUserDetails }) => {
  const [timeframe, setTimeframe] = useState(timeframes[2]);

  const changeTimeframe = (timeframe: string): void => setTimeframe(timeframe);

  const formattedTimeframe = graphqlTimeframe(timeframe);

  const { loading, data } = useAssetQuoteQuery({
    variables: { id: assetId, timeframe: formattedTimeframe }
  });

  const priceHistory = data?.asset?.priceHistory || [];

  const seriesData = priceHistory
    .map(price => ({
      x: moment(price.date)
        .toDate()
        .getTime(),
      y: price.price
    }))
    .sort((a, b) => a.x - b.x);
  const tooltipData = getTooltipData(data?.asset);
  const hasSeriesData = Boolean(seriesData.length);
  const hasTooltipData = Boolean(Object.keys(tooltipData).length);

  return (
    <div className={styles.priceChart}>
      <div className={styles.actionArea}>
        <Button
          analyticsTag="return-to-polls"
          onClick={(): void => toggleMainArea && toggleMainArea(false)}
          text="Return to Poll Questions"
          type={BUTTON_TYPES.LINK}
        />
        <div className={styles.tabbedNav}>
          {loading && <FontAwesomeIcon icon={faSpinner} spin />}
          <TabbedNav
            initialTabIndex={timeframes.findIndex(tab => tab === timeframe)}
            onChange={changeTimeframe}
            tabs={timeframes}
          />
        </div>
      </div>
      <Chart
        height={350}
        series={[{ data: seriesData, type: 'area' }]}
        type="line"
        options={{
          chart: {
            type: 'area',
            fontFamily: 'Lato',
            toolbar: { offsetY: 25 }
          },
          subtitle: {
            text: loading || !hasSeriesData ? '' : readableTimeframe(timeframe),
            style: {
              color: '#3080de'
            }
          },
          tooltip: {
            x: {
              format: 'MMMM d, yyyy'
            },
            custom: ({ series, seriesIndex, dataPointIndex, w }): string => {
              const date = w.globals.seriesX[seriesIndex][dataPointIndex];
              const price = series[seriesIndex][dataPointIndex];

              return renderToString(
                <div className={styles.tooltip}>
                  <ChartTooltipPrice
                    currency={currencyCode}
                    symbol={primarySymbol}
                    price={price}
                    date={moment(new Date(date)).format('MMM D, YYYY')}
                  />
                </div>
              );
            }
          },
          plotOptions: {
            area: {
              fillTo: 'end'
            }
          },
          fill: { opacity: 0.2 },
          title: {
            text: `${titleize(entityName || '')} (${primarySymbol})`,
            style: {
              fontSize: '15px',
              fontWeight: 500
            }
          },
          xaxis: {
            type: 'datetime'
          },
          yaxis: {
            show: hasSeriesData,
            title: { text: 'Closing Price' }
          },
          stroke: {
            curve: 'smooth',
            width: 1
          },
          markers: {
            size: 2
          },
          noData: {
            text: loading ? 'Loading...' : 'Sorry, Pricing Data Is Not Available for This Security'
          }
        }}
      />
      {hasTooltipData && (
        <>
          <DelayedAccessAlert id={Location.priceChart} sectionName="ideas and updates" />
          <div className={styles.ideasInTimeframeHeader}>Ideas Posted/Updated in Timeframe</div>
          <div className={styles.ideasInTimeframe}>
            {tooltipData.map((dataPoint, idx) => {
              const { type, data } = dataPoint;

              switch (type) {
                case 'IDEA':
                  return (
                    <ChartTooltipIdea
                      idea={data}
                      currency={currencyCode}
                      key={idx}
                      showUserDetails={showUserDetails}
                    />
                  );
                case 'IDEA_UPDATE':
                  return (
                    <ChartTooltipIdeaUpdate
                      ideaUpdate={data}
                      key={idx}
                      showUserDetails={showUserDetails}
                    />
                  );
                default:
                  return '';
              }
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default PriceChart;
